import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, ExternalLink, Calendar, Shield } from 'lucide-react';

const DisclaimerPage = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-gorkhali-red via-black to-marigold pt-20 pb-12">
            <div className="max-w-4xl mx-auto px-4">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-12"
                >
                    <div className="inline-flex items-center gap-3 mb-4">
                        <Shield className="text-marigold" size={48} />
                        <h1 className="font-bebas text-6xl text-white tracking-wider">
                            Data Disclaimer
                        </h1>
                    </div>
                    <p className="text-gray-300 text-lg">
                        Information Accuracy & Source Attribution
                    </p>
                </motion.div>

                {/* Main Disclaimer Box */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 mb-8"
                >
                    <div className="flex items-start gap-4 mb-6">
                        <AlertCircle className="text-marigold flex-shrink-0 mt-1" size={32} />
                        <div>
                            <h2 className="font-bebas text-3xl text-white tracking-wider mb-4">
                                Public Information Service
                            </h2>
                            <p className="text-gray-200 text-lg leading-relaxed mb-4">
                                The information presented on this platform is compiled from publicly available
                                sources for the purpose of increasing accessibility to election information.
                            </p>
                        </div>
                    </div>

                    {/* Primary Sources */}
                    <div className="mb-6">
                        <h3 className="font-bebas text-2xl text-marigold tracking-wider mb-3">
                            Primary Data Sources
                        </h3>
                        <ul className="space-y-2 text-gray-200">
                            <li className="flex items-start gap-2">
                                <span className="text-marigold mt-1">•</span>
                                <span>
                                    <strong>Election Commission of Nepal</strong> (election.gov.np) - Official candidate lists and constituency data
                                </span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-marigold mt-1">•</span>
                                <span>
                                    <strong>Official Party Registrations</strong> - Verified political party information
                                </span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-marigold mt-1">•</span>
                                <span>
                                    <strong>Verified News Sources</strong> - The Kathmandu Post, The Himalayan Times, BBC Nepali, Rising Nepal
                                </span>
                            </li>
                        </ul>
                    </div>

                    {/* Accuracy Statement */}
                    <div className="bg-white/5 rounded-lg p-6 mb-6">
                        <h3 className="font-bebas text-2xl text-white tracking-wider mb-3">
                            Accuracy Statement
                        </h3>
                        <p className="text-gray-200 leading-relaxed">
                            We strive to maintain accurate and up-to-date information. However, this platform
                            is <strong>not affiliated</strong> with the Election Commission of Nepal or any political party.
                            For official information, please refer to the{' '}
                            <a
                                href="https://election.gov.np"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-marigold hover:underline inline-flex items-center gap-1"
                            >
                                Election Commission of Nepal
                                <ExternalLink size={14} />
                            </a>.
                        </p>
                    </div>

                    {/* Last Updated */}
                    <div className="flex items-center gap-2 text-gray-300">
                        <Calendar size={20} className="text-marigold" />
                        <span>Last Updated: February 2, 2026</span>
                    </div>
                </motion.div>

                {/* Nepali Version */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8"
                >
                    <h2 className="font-bebas text-3xl text-white tracking-wider mb-4">
                        डाटा अस्वीकरण
                    </h2>
                    <div className="text-gray-200 leading-relaxed space-y-4">
                        <p>
                            यस प्लेटफर्ममा प्रस्तुत जानकारी सार्वजनिक रूपमा उपलब्ध स्रोतहरूबाट संकलित गरिएको हो
                            जसको उद्देश्य निर्वाचन जानकारीमा पहुँच बढाउनु हो।
                        </p>
                        <div>
                            <h3 className="font-bebas text-xl text-marigold tracking-wider mb-2">
                                प्राथमिक डाटा स्रोतहरू
                            </h3>
                            <ul className="space-y-1 ml-4">
                                <li>• निर्वाचन आयोग, नेपाल (election.gov.np)</li>
                                <li>• आधिकारिक पार्टी दर्ता र उम्मेदवार सूची</li>
                                <li>• प्रमाणित समाचार स्रोतहरू</li>
                            </ul>
                        </div>
                        <p>
                            <strong>सटीकता कथन:</strong> हामी सही र अद्यावधिक जानकारी कायम राख्न प्रयास गर्छौं।
                            तर, यो प्लेटफर्म निर्वाचन आयोग नेपाल वा कुनै पनि राजनीतिक दलसँग सम्बद्ध छैन।
                        </p>
                        <p className="text-sm text-gray-400">
                            यो सार्वजनिक जानकारी सेवा हो। सबै डाटा शैक्षिक र सूचनात्मक उद्देश्यका लागि मात्र प्रस्तुत गरिएको छ।
                        </p>
                    </div>
                </motion.div>

                {/* View Sources Link */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="text-center mt-8"
                >
                    <a
                        href="/sources"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-marigold text-black font-bebas text-xl tracking-wider rounded-lg hover:bg-marigold/90 transition-all"
                    >
                        View All Data Sources
                        <ExternalLink size={20} />
                    </a>
                </motion.div>
            </div>
        </div>
    );
};

export default DisclaimerPage;
